import DOMPurify from 'dompurify'

/**
 * Article HTML sanitization — an ARTICLE-SCOPED profile, deliberately separate
 * from `SafeHtml`.
 *
 * `SafeHtml` allows only `p, br, strong, em, b, i, ul, ol, li, a`, which drops an
 * article's headings and has no way to carry a swatch colour. It is also what
 * renders work-order descriptions, so widening it in place would silently expand
 * what those may contain — a security-relevant change made as a side effect of an
 * editorial feature. Hence two profiles.
 *
 * Two things the article profile must carry that ordinary allowlisting cannot
 * express safely are handled by a post-pass over the sanitized markup rather than
 * by admitting the attributes that would express them:
 *
 * - **A swatch colour** is carried as `data-swatch`, never as author `style`. The
 *   post-pass validates it as a hex colour and writes the `background-color`
 *   itself, so an article may contain colours but never arbitrary CSS.
 * - **An inline image's `src`** is pinned to the public asset origin. Author HTML
 *   with an unconstrained `src` is a tracking-pixel and exfiltration vector, so an
 *   image pointing anywhere else is removed.
 *
 * The post-pass runs on a parsed document rather than through a DOMPurify hook on
 * purpose: hooks are registered on the shared DOMPurify instance, so one added
 * here would also fire for `SafeHtml`.
 */

/** Tags the article editor can produce. */
export const ARTICLE_ALLOWED_TAGS = [
	'p',
	'br',
	'strong',
	'em',
	'b',
	'i',
	'u',
	's',
	'a',
	'ul',
	'ol',
	'li',
	'h1',
	'h2',
	'h3',
	'blockquote',
	'pre',
	'code',
	'hr',
	'img',
	'div',
	'span',
]

/**
 * Attributes the article editor can produce. `style` is NOT here: a swatch's
 * colour arrives as `data-swatch` and the post-pass writes the style from a
 * validated value.
 */
export const ARTICLE_ALLOWED_ATTR = [
	'href',
	'target',
	'rel',
	'src',
	'alt',
	'data-article-palette',
	'data-palette',
	'data-swatch',
]

/** A 3- or 6-digit hex colour. The only shape a swatch may carry. */
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

export const isHexColor = (value: string | null | undefined): value is string =>
	typeof value === 'string' && HEX_COLOR.test(value)

/**
 * The origin inline images are WRITTEN to. Absent (unset env var), NO inline
 * image renders: failing closed is the right direction for a check whose purpose
 * is to stop an article fetching from an arbitrary host.
 */
export function publicAssetsOrigin(): string | null {
	const configured = import.meta.env.VITE_PUBLIC_ASSETS_URL
	if (!configured) return null
	try {
		return new URL(configured).origin
	} catch {
		return null
	}
}

/** Virtual-hosted S3 host, region label captured. */
const S3_REGIONAL_HOST = /^([a-z0-9.-]+)\.s3\.[a-z0-9-]+\.amazonaws\.com$/

/**
 * The origins an inline image's `src` may be READ from — the configured one, plus
 * the global `<bucket>.s3.amazonaws.com` alias of the same bucket.
 *
 * Two, not one, because article HTML is persisted with an absolute `src`. Public
 * asset URLs used to be built on the global S3 host; those already-stored articles
 * would lose every image the moment the configured origin became the regional host.
 * Both names address the same bucket, so accepting either widens nothing an author
 * can reach.
 */
export function allowedImageOrigins(): string[] {
	const origin = publicAssetsOrigin()
	if (!origin) return []
	const bucket = new URL(origin).hostname.match(S3_REGIONAL_HOST)?.[1]
	return bucket ? [origin, `https://${bucket}.s3.amazonaws.com`] : [origin]
}

/** The stable, non-expiring URL of a public file, built from its S3 key. */
export function publicAssetUrl(s3Key: string): string | null {
	const origin = publicAssetsOrigin()
	if (!origin) return null
	return `${origin}/${s3Key.replace(/^\/+/, '')}`
}

function isAllowedImageSrc(src: string | null, origins: string[]): boolean {
	if (!src || origins.length === 0) return false
	try {
		const url = new URL(src, origins[0])
		return url.protocol === 'https:' && origins.includes(url.origin)
	} catch {
		return false
	}
}

/**
 * Apply the two rules ordinary allowlisting cannot: write each swatch's
 * background from its validated `data-swatch`, and drop any image whose `src` is
 * not on a public asset origin.
 */
function applyArticleRules(root: ParentNode, origins: string[]): void {
	for (const element of Array.from(root.querySelectorAll('[data-swatch]'))) {
		const value = element.getAttribute('data-swatch')
		if (isHexColor(value)) {
			;(element as HTMLElement).style.backgroundColor = value
		} else {
			// Not a colour: drop the claim rather than leaving a swatch that looks
			// deliberate but renders as nothing.
			element.removeAttribute('data-swatch')
		}
	}

	for (const image of Array.from(root.querySelectorAll('img'))) {
		if (!isAllowedImageSrc(image.getAttribute('src'), origins)) image.remove()
	}
}

/**
 * Sanitize article HTML for rendering. Scripts, event handlers and unknown tags
 * are stripped by DOMPurify; swatch colours and image origins are then enforced
 * by {@link applyArticleRules}.
 */
export function sanitizeArticleHtml(html: string): string {
	const clean = DOMPurify.sanitize(html, {
		ALLOWED_TAGS: ARTICLE_ALLOWED_TAGS,
		ALLOWED_ATTR: ARTICLE_ALLOWED_ATTR,
	})

	const template = document.createElement('template')
	template.innerHTML = clean
	applyArticleRules(template.content, allowedImageOrigins())
	return template.innerHTML
}
